import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { getOS } from '@iconik-io/core/src/os/get_os';
import type { AuthRegistrationSchemaWithId } from '@iconik-io/iconik-api/src/services/auth';
import type { SettingsMergedSettingsSchemaNullable } from '@iconik-io/iconik-api/src/services/settings';
import type { UsersUserSchemaNullable } from '@iconik-io/iconik-api/src/services/users';

import { SHARE_USER_EMAIL_PLACEHOLDER } from '../../react/components/shared/segment/constants';
import { FLAG_MIXPANEL_REPLAYS } from '../flags';
import { growthbook } from '../growthbook';
import type { OnboardingGoal } from '../onboarding_constants';

const logger = DebugLogger.getInstance('segment_tracking');

let analyticsDefaults: Record<string, string | number | boolean> = {};

export function setAnalyticsDefaults(
  defaults: Record<string, string | number | boolean>,
): void {
  analyticsDefaults = { ...analyticsDefaults, ...defaults };
}

export function resetAnalyticsDefaults(): void {
  analyticsDefaults = {};
}

export type ExtraEventOptions = {
  integrations: Record<string, boolean>;
};

export type SegmentUserData = {
  type?: string;
  is_admin?: boolean;
  system_domain_id?: string | null;
  date_created?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email_marketing_consent?: boolean;
  onboarding_goal?: OnboardingGoal | null;
};

type SegmentPropertyValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | readonly string[]
  | readonly number[];

export type SegmentEventOptions = {
  eventName: string;
  properties?: Record<string, SegmentPropertyValue>;
};

// A share session fills this from the share token, which has none of these
// user fields. Mirrors AuthUserData in web's ducks/auth/types.ts.
type FieldsMissingFromShareToken = 'groups' | 'metadata' | 'system_domains';

type AuthUserData = Omit<UsersUserSchemaNullable, FieldsMissingFromShareToken> &
  Partial<Pick<UsersUserSchemaNullable, FieldsMissingFromShareToken>> & {
    shareEmail?: string;
  };

const isMixpanelDisabled = (userData: AuthUserData) =>
  Boolean(userData.disable_mixpanel);

function getEmailForAnalyticEvents(email: string | undefined | null) {
  // Do not send the placeholder email to Segment, otherwise anonymous events
  // completed by shareable link users will all end up under one profile in Mixpanel
  if (email === SHARE_USER_EMAIL_PLACEHOLDER) {
    return undefined;
  }
  return email;
}

export const segmentRegistrationUserData = (
  userData: AuthUserData | AuthRegistrationSchemaWithId,
): SegmentUserData => {
  return {
    date_created: userData.date_created,
    email: userData.email,
    first_name: userData.first_name,
    last_name: userData.last_name,
    email_marketing_consent: Boolean(userData.email_marketing_consent),
  };
};

export const getSegmentUserData = (userData: AuthUserData): SegmentUserData => {
  const email = getEmailForAnalyticEvents(userData.email);

  if (userData.shareEmail) {
    return {
      system_domain_id: userData.system_domain_id,
      email,
    };
  }

  return {
    ...segmentRegistrationUserData(userData),
    type: userData.type,
    is_admin: Boolean(userData.is_admin),
    system_domain_id: userData.system_domain_id,
    onboarding_goal: userData.onboarding_goal,
  };
};

export const getSegmentExtraOptions = (userData: AuthUserData) => {
  const extraEventOptions: ExtraEventOptions = { integrations: { All: true } };
  if (isMixpanelDisabled(userData) || (!userData.id && !userData.shareEmail)) {
    extraEventOptions.integrations.Mixpanel = false;
  }
  return extraEventOptions;
};

export const basicSegmentTrackEvent = (args: {
  options: SegmentEventOptions;
  extraOpts?: ExtraEventOptions;
  userData?: AuthUserData;
}) => {
  if (!window.analytics) {
    return;
  }
  const { options, extraOpts, userData } = args;
  const { eventName, properties } = options;

  logger.debug('Track event', { eventName, properties, analyticsDefaults });

  const extraOptions =
    extraOpts ?? (userData ? getSegmentExtraOptions(userData) : undefined);

  if (userData?.id) {
    analytics.track(
      eventName,
      {
        ...analyticsDefaults,
        ...properties,
        'System Domain ID': userData?.system_domain_id ?? null,
        Email: getEmailForAnalyticEvents(userData?.email),
        Platform: window.PLATFORM,
      },
      extraOptions,
    );
  } else {
    // Bypass Segment for anonymous users and send directly to Mixpanel.
    // This is to avoid overage fees related to tracking a large number of anonymous users through Segment.
    const trackEvent = () => {
      window.mixpanel?.track(eventName, {
        ...analyticsDefaults,
        ...properties,
        distinct_id: analytics.user().anonymousId(),
        Platform: window.PLATFORM,
      });
    };
    if (!analytics.user) {
      analytics.ready(trackEvent);
    } else {
      trackEvent();
    }
  }
};

type BasicSegmentTrackPageParams = {
  page: {
    title?: string;
    entityId?: string;
    referrer?: string;
    route?: string;
  };
  userData?: AuthUserData;
};

export const basicSegmentTrackPage = (params?: BasicSegmentTrackPageParams) => {
  if (!window.analytics) {
    logger.debug('Analytics not initialized, skipping page tracking');
    return;
  }
  logger.debug('Track page', params);
  const { page, userData } = params ?? {};
  const { href, pathname } = document.location;
  const osPlatform = getOS();
  const extraOpts = userData ? getSegmentExtraOptions(userData) : undefined;

  const pageProperties = {
    title: page?.title,
    url: href,
    path: pathname,
    referrer: page?.referrer,
    page: page?.entityId,
    Route: page?.route,
    $screen_height: window.screen.height,
    $screen_width: window.screen.width,
    $os: osPlatform,
  };

  if (userData?.id) {
    analytics.page(
      page?.title,
      { ...analyticsDefaults, ...pageProperties },
      extraOpts,
    );
  } else {
    // Bypass Segment for anonymous users and send directly to Mixpanel.
    // This is to avoid overage fees related to tracking a large number of anonymous users through Segment.
    const trackPageView = () => {
      window.mixpanel?.track_pageview(
        {
          ...analyticsDefaults,
          ...pageProperties,
          distinct_id: analytics.user().anonymousId(),
        },
        {
          event_name: 'Loaded a Page',
        },
      );
    };
    if (!analytics.user) {
      analytics.ready(trackPageView);
    } else {
      trackPageView();
    }
  }
};

export const basicSegmentIdentifyUser = (args: {
  segmentUserData: SegmentUserData;
  extraOpts?: ExtraEventOptions;
  userData?: AuthUserData;
  settings?: SettingsMergedSettingsSchemaNullable;
}) => {
  if (!window.analytics) {
    return;
  }
  const { userData, settings, segmentUserData, extraOpts } = args;
  const extraOptions =
    extraOpts ?? (userData ? getSegmentExtraOptions(userData) : undefined);

  const eventProps: Record<string, any> = {
    'User Type': segmentUserData.type,
    'System Domain ID': segmentUserData.system_domain_id,
    'Is Administrator': segmentUserData.is_admin,
    'Account Created': segmentUserData.date_created,
    'First Name': segmentUserData.first_name,
    'Last Name': segmentUserData.last_name,
    'Email Marketing Consent': segmentUserData.email_marketing_consent,
    'Onboarding Goal': segmentUserData.onboarding_goal,
    Email: segmentUserData.email,
  };

  const plgDomain = settings?.is_plg_domain;
  if (settings) {
    // PLG users are either self-registered or invited by an existing user
    const genesis = settings?.genesis;

    // When is_plg_domain is true, the domain creation was product-led
    // ie. did not involve a salesperson
    eventProps['Sales Led'] = plgDomain === undefined ? undefined : !plgDomain;
    eventProps.Genesis = genesis;
  }

  const userId = userData?.id;

  logger.debug('Identify user', { userId, eventProps });

  if (userId) {
    analytics.identify(userId, eventProps, extraOptions);

    // start a mixpanel session replay capture if PLG user and flag enabled
    const recordSessions = growthbook?.isOn(FLAG_MIXPANEL_REPLAYS) ?? false;
    if (plgDomain && recordSessions) {
      window.mixpanel?.identify(userId);
      window.mixpanel?.start_session_recording?.();
    }
  } else {
    // anonymous app user
    analytics.identify(eventProps, extraOptions);
  }

  if (segmentUserData.system_domain_id) {
    analytics.group(
      segmentUserData.system_domain_id,
      {
        'System Domain ID': segmentUserData.system_domain_id,
      },
      extraOptions,
    );
  }
};
